import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { productService } from '../../../services/productService';
import { supabase } from '../../../lib/supabase';

const HeroSection = ({ title, subtitle, description, cta_text, cta_link }) => { // ✅ UPDATED: Receive props
  const navigate = useNavigate();
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [featuredModels, setFeaturedModels] = useState([]);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({ products: '50K+', parts: '15K+', possibilities: '2M+' });

  // Fetch live featured products from database
  useEffect(() => {
    const fetchFeaturedModels = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch metrics
        const { count: productCount } = await supabase.from('products').select('*', { count: 'exact', head: true });
        
        // Fetch aggregation of component_count from products table
        const { data: productsData } = await supabase.from('products').select('component_count');
        const totalParts = productsData?.reduce((sum, item) => sum + (item.component_count || 0), 0) || 0;

        // Calculate dynamic possibilities (products count multiplied by total parts count)
        const productNum = productCount !== null ? productCount : 50;
        const partsNum = totalParts > 0 ? totalParts : 15000;
        const totalPossibilities = productNum * partsNum;
        
        let formattedPossibilities = '2M+';
        if (totalPossibilities >= 1000000) {
          formattedPossibilities = `${(totalPossibilities / 1000000).toFixed(1)}M+`;
        } else if (totalPossibilities >= 1000) {
          formattedPossibilities = `${(totalPossibilities / 1000).toFixed(0)}K+`;
        } else if (totalPossibilities > 0) {
          formattedPossibilities = `${totalPossibilities}+`;
        }

        setMetrics(prev => ({
          ...prev,
          products: productCount !== null ? `${productCount.toLocaleString()}+` : prev.products,
          parts: totalParts > 0 ? `${totalParts.toLocaleString()}+` : prev.parts,
          possibilities: formattedPossibilities
        }));

        // Fetch featured products with thumbnails
        const response = await productService?.getAll({
          isFeatured: true,
          itemsPerPage: 6 // Fetch up to 6 featured models for rotation
        });

        if (response?.products && response?.products?.length > 0) {
          // Transform API response to match component's data structure
          const transformedModels = response?.products?.map((product) => ({
            id: product?.id,
            name: product?.name || 'Untitled Product',
            category: product?.category?.name || 'General',
            image: product?.thumbnail || product?.thumbnailPath || "/assets/images/model-fallback.jpg",
            alt: `${product?.name || 'Product'} - ${product?.description?.substring(0, 50) || 'High-quality 3D model'}`,
            components: product?.componentCount || 0, // ✅ CHANGED: from sold to components
            rating: product?.averageRating || 0,
            creator: product?.manufacturer?.companyName || product?.seller?.name || 'OpenGadget'
          }));

          setFeaturedModels(transformedModels);
        } else {
          // Fallback: Try to get any products if no featured ones
          const fallbackResponse = await productService?.getAll({
            itemsPerPage: 3
          });

          if (fallbackResponse?.products && fallbackResponse?.products?.length > 0) {
            const transformedModels = fallbackResponse?.products?.map((product) => ({
              id: product?.id,
              name: product?.name || 'Untitled Product',
              category: product?.category?.name || 'General',
              image: product?.thumbnail || product?.thumbnailPath || "/assets/images/model-fallback.jpg",
              alt: `${product?.name || 'Product'} - ${product?.description?.substring(0, 50) || 'High-quality 3D model'}`,
              components: product?.componentCount || 0, // ✅ CHANGED: from sold to components
              rating: product?.averageRating || 0,
              creator: product?.manufacturer?.companyName || product?.seller?.name || 'OpenGadget'
            }));

            setFeaturedModels(transformedModels);
          } else {
            // Last resort: Static fallback if no products in database
            setFeaturedModels([
            {
              id: 1,
              name: "Sample Product",
              category: "Electronics",
              image: "/assets/images/hero-bg.jpg",
              alt: "Sample product showcase",
              components: 0, // ✅ CHANGED: from sold to components
              rating: 5.0,
              creator: "OpenGadget"
            }]
            );
          }
        }
      } catch (err) {
        console.error('❌ Failed to fetch featured models:', err);
        setError('Unable to load featured products');

        // Use static fallback on error
        setFeaturedModels([
        {
          id: 1,
          name: "Sample Product",
          category: "Electronics",
          image: "/assets/images/hero-bg.jpg",
          alt: "Sample product showcase",
          components: 0, // ✅ CHANGED: from sold to components
          rating: 5.0,
          creator: "OpenGadget"
        }]
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedModels();
  }, []);

  useEffect(() => {
    if (featuredModels?.length > 0) {
      const interval = setInterval(() => {
        setCurrentModelIndex((prev) => (prev + 1) % featuredModels?.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [featuredModels?.length]);

  const currentModel = featuredModels?.[currentModelIndex];

  // NEW: "Try 3D Viewer" button handler - randomly picks a featured model
  const handleTry3DViewer = async () => {
    try {
      console.log('🎯 Try 3D Viewer button clicked - fetching featured products...');

      // Fetch featured products with 3D models
      const featuredProducts = await productService?.getAll({
        isFeatured: true,
        has3DModel: true
      });

      console.log('📦 Featured products with 3D models:', featuredProducts?.products?.length || 0);

      if (!featuredProducts?.products || featuredProducts?.products?.length === 0) {
        console.log('⚠️ No featured products found, trying all products with 3D models...');

        // Fallback: Get any product with a 3D model
        const anyProducts = await productService?.getAll({
          has3DModel: true,
          itemsPerPage: 10
        });

        console.log('📦 All products with 3D models:', anyProducts?.products?.length || 0);

        if (!anyProducts?.products || anyProducts?.products?.length === 0) {
          console.error('❌ No 3D models available in database');
          alert('No 3D models available at the moment. Please check back later or contact support to add 3D models to the catalog.');
          return;
        }

        // Pick random product from any available
        const randomIndex = Math.floor(Math.random() * anyProducts?.products?.length);
        const randomProduct = anyProducts?.products?.[randomIndex];

        console.log('✅ Randomly selected product (from all):', {
          id: randomProduct?.id,
          name: randomProduct?.name,
          modelUrl: randomProduct?.modelUrl
        });

        navigate(`/3d-model-viewer?id=${randomProduct?.id}`);
        return;
      }

      // Pick random featured product
      const randomIndex = Math.floor(Math.random() * featuredProducts?.products?.length);
      const selectedProduct = featuredProducts?.products?.[randomIndex];

      console.log('✅ Randomly selected product (from featured):', {
        id: selectedProduct?.id,
        name: selectedProduct?.name,
        modelUrl: selectedProduct?.modelUrl
      });

      // Navigate to 3D viewer with selected product
      navigate(`/3d-model-viewer?id=${selectedProduct?.id}`);
    } catch (err) {
      console.error('❌ Failed to load featured model:', err);
      alert('Unable to load 3D viewer. Please try again or contact support if the issue persists.');
    }
  };

  if (isLoading) {
    return (
      <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <div className="space-y-2">
            <div className="h-4 bg-slate-700 rounded w-48 mx-auto animate-pulse"></div>
            <div className="h-3 bg-slate-700 rounded w-32 mx-auto animate-pulse"></div>
          </div>
        </div>
      </section>);

  }

  // Use props or default hardcoded values if props are not provided
  const displayTitle = title || 'Repair Parts Like you Shop.';
  const displaySubtitle = subtitle || 'Swipe, Swap, Done.';
  const displayDescription = description || 'Transform your creative vision with our collection of 3D parts from within inside the models. Easily discover the perfect components with zero guesswork. OpenGadget makes high-precision repairs possible to everyone by transforming technical part sourcing into a seamless and friendly shopping experience.';
  const displayCtaText = cta_text || 'Search';
  const displayCtaLink = cta_link || '/model-catalog';

  return (
    <section className="relative min-h-[85vh] bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>
      <div className="relative z-10 container mx-auto px-6 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Icon name="Sparkles" size={16} />
                <span>New 3D Models Added Daily</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                {displayTitle}
                <span className="text-gradient block">{displaySubtitle}</span>
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed">
                {displayDescription}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={displayCtaLink}>
                <Button variant="default" size="lg" iconName="Search" iconPosition="left" className="w-full sm:w-auto">
                  {displayCtaText}
                </Button>
              </Link>
              
              <Button
                variant="outline"
                size="lg"
                iconName="Eye"
                iconPosition="left"
                className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10"
                onClick={handleTry3DViewer}>

                Try 3D Viewer
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{metrics.products}</div>
                <div className="text-sm text-slate-400">Products</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{metrics.parts}</div>
                <div className="text-sm text-slate-400">Parts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{metrics.possibilities}</div>
                <div className="text-sm text-slate-400">Possibilities</div>
              </div>
            </div>
          </div>

          {/* 3D Model Showcase */}
          <div className="relative">
            <div 
              className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 shadow-2xl"
            >
              <div 
                className="aspect-square rounded-2xl overflow-hidden bg-slate-800/50 cursor-pointer "
                onClick={() => navigate(`/3d-model-viewer?id=${currentModel?.id}`)}
                title="Click to view 3D Model"
              >
                <Image
                  src={currentModel?.image}
                  alt={currentModel?.alt}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
              
              {/* Model Info Overlay */}
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">{currentModel?.name}</h3>
                    <p className="text-sm text-slate-300">by {currentModel?.creator}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-1 text-amber-400">
                      <Icon name="Star" size={14} />
                      <span className="text-sm font-medium">{currentModel?.rating}</span>
                    </div>
                    <p className="text-xs text-slate-400">{currentModel?.components?.toLocaleString() || 0} components</p> {/* ✅ CHANGED: to components */}
                  </div>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {featuredModels?.map((_, index) =>
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentModelIndex(index); }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentModelIndex ?
                  'bg-primary scale-125' : 'bg-white/30 hover:bg-white/50'}`
                  } />

                )}
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent/20 rounded-full animate-float"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-primary/10 rounded-full animate-pulse-soft"></div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={24} color="white" className="opacity-60" />
      </div>
    </section>);

};

export default HeroSection;